import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { getDoctor, getAvailableSlots, createAppointment } from "../../../api/api";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";

const STEPS = ["Slot", "Patient", "Review"];
const VISIT_TYPES = [
  { key: "center", label: "Center Visit" },
  { key: "video", label: "Online" },
  { key: "home", label: "Home Visit" },
  { key: "walkin", label: "Walk In" },
];

const dateRange = () => {
  const today = moment();
  return Array.from({ length: 10 }, (_, i) => today.clone().add(i - 3, "days"));
};

// Cut-down OpdBooking: skips doctor selection (implicit — this doctor's own
// linked record), keeps the same slot-picker → patient form → review steps
// and hits the same createAppointment endpoint. The backend forces
// doctor_id to this account's own linked doctor regardless of what's sent.
export default function MyBooking() {
  const doctorId = useSelector((s) => s.users.user?.doctor_id);
  const [doctor, setDoctor] = useState(null);
  const [step, setStep] = useState(1);
  const [visitType, setVisitType] = useState("center");
  const [isoDate, setIsoDate] = useState(moment().format("YYYY-MM-DD"));
  const [slotInfo, setSlotInfo] = useState({ onLeave: false, closed: false, slots: [] });
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [patient, setPatient] = useState({ name: "", phone: "", email: "", gender: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!doctorId) return;
    getDoctor(doctorId)
      .then((res) => setDoctor(res.data))
      .catch((err) => toast.error(err?.message || "Could not load your doctor profile."));
  }, [doctorId]);

  useEffect(() => {
    if (!doctorId || step !== 1) return;
    getAvailableSlots({ doctor_id: doctorId, iso_date: isoDate })
      .then((res) => {
        setSlotInfo(res.data);
        setSelectedSlot(null);
      })
      .catch((err) => toast.error(err?.message || "Could not load available slots."));
  }, [doctorId, isoDate, step]);

  const canProceed = () => {
    if (step === 1) return !!selectedSlot;
    if (step === 2) return patient.name && patient.phone.length >= 10;
    return true;
  };

  const handleNext = () => {
    if (step === 3) return handleConfirm();
    setStep((s) => Math.min(s + 1, 3));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await createAppointment({
        doctor_id: doctorId,
        iso_date: isoDate,
        slot_time: selectedSlot,
        visit_type: visitType,
        patient: {
          name: patient.name,
          phone: patient.phone,
          email: patient.email || undefined,
          gender: patient.gender || undefined,
        },
      });
      setSuccess(res.data);
      toast.success("Appointment booked.");
    } catch (err) {
      toast.error(err?.message || "Could not confirm booking.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setVisitType("center");
    setIsoDate(moment().format("YYYY-MM-DD"));
    setSlotInfo({ onLeave: false, closed: false, slots: [] });
    setSelectedSlot(null);
    setPatient({ name: "", phone: "", email: "", gender: "" });
    setSuccess(null);
  };

  if (success) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary/10 text-secondary">
          <Check className="h-7 w-7" />
        </span>
        <h2 className="font-serif text-2xl font-semibold">Booking confirmed</h2>
        <p className="mt-1 text-sm text-muted-foreground">Appointment #{success._id.slice(-6)}</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button variant="outline" onClick={resetWizard}>
            Book another
          </Button>
          <Button onClick={() => navigate("/my-appointments")}>Go to my appointments</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold">Book a patient</h2>
        <p className="text-sm text-muted-foreground">Book a consultation on your own schedule</p>
      </div>

      <div>
        <div className="mb-2 flex justify-between text-xs font-medium text-muted-foreground">
          {STEPS.map((label, i) => (
            <span key={label} className={i + 1 <= step ? "text-primary" : ""}>
              {i + 1}. {label}
            </span>
          ))}
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-primary transition-all" style={{ width: `${(step / 3) * 100}%` }} />
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-5">
          <div className="flex gap-2">
            {VISIT_TYPES.map((v) => (
              <button
                key={v.key}
                onClick={() => setVisitType(v.key)}
                className={cn(
                  "rounded-full border px-4 py-1.5 text-sm font-medium",
                  visitType === v.key ? "border-primary bg-primary text-primary-foreground" : "border-border"
                )}
              >
                {v.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {dateRange().map((d) => {
              const iso = d.format("YYYY-MM-DD");
              const isPast = d.isBefore(moment(), "day");
              const active = iso === isoDate;
              return (
                <button
                  key={iso}
                  disabled={isPast}
                  onClick={() => setIsoDate(iso)}
                  className={cn(
                    "flex w-16 shrink-0 flex-col items-center rounded-lg border py-2 text-sm",
                    isPast && "opacity-40",
                    active ? "border-primary bg-primary/5" : "border-border"
                  )}
                >
                  <span className="text-xs text-muted-foreground">{d.format("ddd")}</span>
                  <span className="font-medium">{d.format("D")}</span>
                </button>
              );
            })}
          </div>

          {slotInfo.closed ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              The clinic is closed this day. Please pick another date.
            </p>
          ) : slotInfo.onLeave ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              You're marked on leave this day. Please pick another date.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slotInfo.slots.map((s) => (
                <button
                  key={s.time}
                  disabled={s.taken}
                  onClick={() => setSelectedSlot(s.time)}
                  className={cn(
                    "rounded-md border px-3 py-1.5 text-sm",
                    s.taken && "cursor-not-allowed opacity-40 line-through",
                    selectedSlot === s.time
                      ? "border-primary bg-primary text-primary-foreground"
                      : s.vip
                        ? "border-warning/50 bg-warning/10 text-warning"
                        : "border-border"
                  )}
                >
                  {s.vip && <span className="font-semibold">VIP Slot · </span>}
                  {s.time}
                </button>
              ))}
            </div>
          )}

          {doctor && (
            <div className="rounded-md bg-muted p-3 text-sm">
              Consult fee: <b>₹{doctor.fee}</b>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-sm font-medium">Name *</label>
            <Input value={patient.name} onChange={(e) => setPatient((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Mobile *</label>
            <Input value={patient.phone} onChange={(e) => setPatient((p) => ({ ...p, phone: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email</label>
            <Input type="email" value={patient.email} onChange={(e) => setPatient((p) => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Gender</label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={patient.gender}
              onChange={(e) => setPatient((p) => ({ ...p, gender: e.target.value }))}
            >
              <option value="">Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-1 rounded-lg border border-border p-5">
          {[
            ["Visit type", VISIT_TYPES.find((v) => v.key === visitType)?.label],
            ["Date & time", `${moment(isoDate).format("D MMM YYYY")}, ${selectedSlot}`],
            ["Patient name", patient.name],
            ["Mobile number", patient.phone],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between border-b border-border py-2 text-sm last:border-0">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-medium">{value}</span>
            </div>
          ))}
          {doctor && (
            <div className="flex justify-between pt-3 text-base font-semibold">
              <span>Total payable</span>
              <span>₹{doctor.fee}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between border-t border-border pt-4">
        <Button variant="outline" disabled={step === 1} onClick={() => setStep((s) => s - 1)}>
          Back
        </Button>
        <Button disabled={!canProceed() || submitting} onClick={handleNext}>
          {step === 3 ? (submitting ? "Confirming…" : "Confirm booking") : "Next"}
        </Button>
      </div>
    </div>
  );
}
